import React, { useCallback, useEffect, useState } from "react";
import { injectIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { useAuthDataContext, RestrictedToAdmin } from "components/auth";
import Card from "components/card";
import DataGrid, { DataGridItem } from "components/data-grid";
import ValidatedForm from "components/form";
import FormTextField from "components/form/form-textfield";
import FormPasswordField from "components/form/form-password-field";
import { PartnerSelect, RoleSelectMultiple } from "components/select";
import withStyles from "components/with-styles";
import styles from "./styles/common-form.css.js";
import validationSchema from "./validation-schema";
import { TextDisplay } from "components/display-data";
import SelectComponent from "../../../../components/select/SelectComponent";
import CustomerSelect from "../../../../components/select/customer-select";
import { useCurrentCustomerContext } from "../../../../components/current-customer-context";
import { ROLE_PARTNER_USER, ROLE_SUPER_USER } from "../../../../utils/constants";

const isPartnerRoleSelected = ({ roles, selectedRoleIds }) =>
  selectedRoleIds.map(selectedRoleId => roles[selectedRoleId]).includes(ROLE_PARTNER_USER);

const isSuperuserRoleSelected = ({ roles, selectedRoleIds }) =>
  selectedRoleIds.map(selectedRoleId => roles[selectedRoleId]).includes(ROLE_SUPER_USER);

const CustomerContractSelector = ({ intl, id, handleChange, values, classes }) => {
  const { customers, contracts } = useCurrentCustomerContext();
  const customerDescription = customers?.find(customer => customer._id === id)?.description;

  const userContracts = contracts.filter(contract => values.serviceContractsFilter.includes(contract._id));
  const customerContracts = contracts.filter(contract => contract.customer_id === id);
  const isEmptyCustomer = !customerContracts.length;

  const contractsToRemove = userContracts.filter(contract => !values.customerIds.includes(contract.customer_id));

  useEffect(() => {
    if (isEmptyCustomer) {
      const contractsToAdd = contracts.filter(contract => contract.customer_id === id).map(contract => contract._id);
      handleChange("serviceContractsFilter")(values.serviceContractsFilter.concat(contractsToAdd));
    }
    return () => {
      handleChange("serviceContractsFilter")(
        values.serviceContractsFilter.filter(contract => !contractsToRemove.includes(contract)),
      );
    };
  }, []);

  return (
    <DataGrid>
      <DataGridItem small className={classes.customerTextContainer}>
        <TextDisplay label={intl.formatMessage({ id: "app.customer" })}>{customerDescription}</TextDisplay>
      </DataGridItem>
      <DataGridItem threeQuarter>
        <SelectComponent
          multiple
          label={intl.formatMessage({ id: "user.contracts" })}
          id="serviceContractsFilter"
          key={values.serviceContractsFilter}
          value={values.serviceContractsFilter}
          handleChange={handleChange}
          name="serviceContractsFilter"
          elements={customerContracts}
          getValueFromElement={item => item._id}
          getLabelFromElement={item => item.description}
          itemKey="_id"
          itemValue="_id"
          itemLabel="description"
        />
      </DataGridItem>
    </DataGrid>
  );
};

const CommonForm = ({ classes, initialValues, intl, isNewForm, onSubmit }) => {
  const { hasAdminPrivileges } = useAuthDataContext();

  const [roles, setRoles] = useState({});
  const [partnerRoleSelected, setPartnerRoleSelected] = useState(false);
  const [superuserRoleSelected, setSuperuserRoleSelected] = useState(true);

  useEffect(
    () => {
      if (!!initialValues?.roleIds?.length && !!Object.keys(roles)?.length) {
        setPartnerRoleSelected(isPartnerRoleSelected({ roles, selectedRoleIds: initialValues.roleIds }));
        setSuperuserRoleSelected(isSuperuserRoleSelected({ roles, selectedRoleIds: initialValues.roleIds }));
      }
    },
    [initialValues, roles],
  );

  const handleRolesLoaded = useCallback(data => {
    data?.items && setRoles(data.items.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {}));
  }, []);

  return (
    <ValidatedForm
      initialValues={initialValues}
      validationSchema={validationSchema({
        hasAdminPrivileges,
        intl,
        isNewForm,
        partnerRoleSelected,
      })}
      onSubmit={onSubmit}
    >
      {({ handleChange, values }) => (
        <DataGrid>
          {isNewForm && (
            <DataGridItem>
              <FormTextField
                type="text"
                id="username"
                name="username"
                value={values.username}
                onChange={handleChange}
                label={intl.formatMessage({ id: "user.username" })}
              />
            </DataGridItem>
          )}
          <DataGridItem>
            <FormTextField
              type="text"
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.name" })}
            />
          </DataGridItem>
          {!isNewForm && <DataGridItem />}
          <DataGridItem>
            <FormTextField
              type="email"
              id="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.contact.email" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormTextField
              type="number"
              id="telephone"
              name="telephone"
              value={values.telephone}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.contact.telephone" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormPasswordField
              showRequirements
              id="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              label={intl.formatMessage({ id: "user.password" })}
            />
          </DataGridItem>
          <DataGridItem>
            <FormPasswordField
              id="confirmPassword"
              name="confirmPassword"
              value={values.confirmPassword}
              onChange={handleChange}
              label={intl.formatMessage({ id: "user.confirmPassword" })}
            />
          </DataGridItem>
          <DataGridItem>
            <RoleSelectMultiple
              className={classes.multipleSelect}
              isInsideForm
              label={intl.formatMessage({
                id: "user.roles",
              })}
              onChange={name => value => {
                const partnerRoleSelected = isPartnerRoleSelected({ roles, selectedRoleIds: value });
                const fieldToReset = partnerRoleSelected ? "ownedByPartnerId" : "partnerId";

                const superuserRoleSelected = isSuperuserRoleSelected({ roles, selectedRoleIds: value });

                handleChange(fieldToReset)(null);
                handleChange(name)(value);

                setSuperuserRoleSelected(superuserRoleSelected);
                setPartnerRoleSelected(partnerRoleSelected);
              }}
              onQueryDone={handleRolesLoaded}
              value={values.roleIds}
            />
          </DataGridItem>
          {!superuserRoleSelected ? (
            <>
              <DataGridItem big>
                <CustomerSelect
                  multiple
                  name="customerIds"
                  label={intl.formatMessage({
                    id: "user.customers",
                  })}
                  id="customerIds"
                  handleChange={handleChange}
                  value={values.customerIds}
                />
              </DataGridItem>
              <DataGridItem big>
                {values?.customerIds?.map(id => (
                  <CustomerContractSelector
                    key={id}
                    intl={intl}
                    id={id}
                    handleChange={handleChange}
                    values={values}
                    classes={classes}
                  />
                ))}
              </DataGridItem>
            </>
          ) : (
            <DataGridItem big>
              <Typography variant="body1">{intl.formatMessage({ id: "user.access.allCustomers" })}</Typography>
            </DataGridItem>
          )}
          <RestrictedToAdmin excludePartners>
            <Card className={classes.card}>
              <DataGrid>
                <DataGridItem>
                  <Typography variant="h6">
                    <FormattedMessage id="app.page.user.associateToPartner" />
                  </Typography>
                  <PartnerSelect
                    disabled={partnerRoleSelected}
                    id="ownedByPartnerId"
                    name="ownedByPartnerId"
                    isInsideForm
                    label={intl.formatMessage({ id: "app.partner" })}
                    onChange={handleChange}
                    value={values.ownedByPartnerId}
                  />
                </DataGridItem>
                {partnerRoleSelected && (
                  <RestrictedToAdmin excludePartners>
                    <DataGridItem>
                      <Typography variant="h6">
                        <FormattedMessage id="app.partner" />
                      </Typography>
                      <PartnerSelect
                        id="partnerId"
                        name="partnerId"
                        isInsideForm
                        label={intl.formatMessage({
                          id: "app.partner",
                        })}
                        onChange={handleChange}
                        value={values.partnerId}
                      />
                    </DataGridItem>
                  </RestrictedToAdmin>
                )}
              </DataGrid>
            </Card>
          </RestrictedToAdmin>
        </DataGrid>
      )}
    </ValidatedForm>
  );
};

CommonForm.propTypes = {
  /**
   * Valores iniciales del formulario.
   */
  initialValues: PropTypes.object,
  /**
   * Indica si es el formulario de creación.
   */
  isNewForm: PropTypes.bool,
  /**
   * Callback de submit del formulario.
   */
  onSubmit: PropTypes.func.isRequired,
};

export default withStyles(styles)(injectIntl(CommonForm));
